/**
 * Tab Control Component
 * Renders named tab panels where each tab contains nested child controls.
 * Data structure: value is an object keyed by tab propName.
 * Schema structure: control.childs is an object keyed by tab propName.
 */

import React, { useState } from 'react';
import { Box, Tab, Tabs } from '@mui/material';
import { ControlComponentProps } from '../../../../types/ControlTypes';
import { LabelWithInfo } from './LabelWithInfo';

export const TabControl: React.FC<ControlComponentProps> = ({
  name,
  path,
  value,
  onChange,
  error,
  disabled,
  readonly,
  label,
  description,
  required,
  control,
  renderChild,
}) => {
  // childs is an object: { tabName: fieldSchemaEntry, ... }
  const childs: Record<string, any> = control?.childs || {};
  const tabKeys = Object.keys(childs);

  const [activeTab, setActiveTab] = useState(0);

  const currentValue = typeof value === 'object' && value !== null ? value : {};

  const handleChildChange = (tabKey: string, newTabValue: any) => {
    onChange({ ...currentValue, [tabKey]: newTabValue });
  };

  if (tabKeys.length === 0) {
    return (
      <Box sx={{ p: 2, color: '#999' }}>
        <LabelWithInfo label={label || name} description={description} required={required} />
        No tabs defined
      </Box>
    );
  }

  const activeKey = tabKeys[activeTab] ?? tabKeys[0];
  const activeSchema = childs[activeKey];

  return (
    <Box sx={{ mb: 2 }}>
      {(label || description) && (
        <Box sx={{ mb: 1 }}>
          <LabelWithInfo label={label || name} description={description} required={required} />
        </Box>
      )}

      <Box
        sx={{
          border: '1px solid #e0e0e0',
          borderRadius: 2,
          overflow: 'hidden',
          backgroundColor: '#fff',
        }}
      >
        {/* Tab header */}
        <Tabs
          value={activeTab}
          onChange={(_, newValue) => setActiveTab(newValue)}
          variant="scrollable"
          scrollButtons="auto"
          sx={{
            borderBottom: '1px solid #e0e0e0',
            backgroundColor: '#fafafa',
            minHeight: 40,
            '& .MuiTab-root': {
              minHeight: 40,
              textTransform: 'none',
              fontSize: 13,
              fontWeight: 500,
              color: '#555',
            },
            '& .Mui-selected': {
              color: '#1976d2',
              fontWeight: 600,
            },
            '& .MuiTabs-indicator': {
              backgroundColor: '#1976d2',
            },
          }}
        >
          {tabKeys.map((key, idx) => (
            <Tab key={key} label={key} id={`tab-${idx}`} />
          ))}
        </Tabs>

        {/* Tab content */}
        <Box sx={{ p: 2 }}>
          {renderChild ? (
            renderChild({
              schema: activeSchema,
              value: currentValue[activeKey],
              onChange: (newVal: any) => handleChildChange(activeKey, newVal),
              fieldPath: `${path}.${activeKey}`,
            })
          ) : (
            <Box sx={{ p: 2, color: '#999', border: '1px solid #ff9999' }}>
              ⚠️ renderChild not provided
            </Box>
          )}
        </Box>
      </Box>

      {error && (
        <Box sx={{ mt: 0.5, fontSize: 12, color: '#d32f2f' }}>{error}</Box>
      )}
    </Box>
  );
};
